import { normalizePartKind, type HomePartKind } from "@/home/part-kind";
import { partById, partFootprint, type GridRect } from "@/home/placement";
import type { HomePartDefinition, HomeRoomData } from "@/home/storage";

export type PartSocket = {
  id: string;
  /** Offset from placement top-left (grid cells). */
  dx: number;
  dy: number;
  accept: HomePartKind[];
};

export function defaultSocketsForPart(part: HomePartDefinition): PartSocket[] {
  if (normalizePartKind(part.kind) !== "solid") return [];
  const fp = partFootprint(part);
  const sockets: PartSocket[] = [];
  // Front edge = bottom rows of footprint (closer to camera / higher gridY).
  const frontRow = Math.max(0, fp.h - 1);
  for (let x = 0; x < fp.w; x += 1) {
    sockets.push({
      id: `front-${x}`,
      dx: x,
      dy: frontRow,
      accept: ["prop"],
    });
  }
  // Doorstep just in front of building when space allows (dy + 1 clamped by caller).
  if (fp.w >= 1) {
    sockets.push({
      id: "doorstep",
      dx: Math.floor((fp.w - 1) / 2),
      dy: frontRow,
      accept: ["prop"],
    });
  }
  return sockets;
}

export function socketsForPart(part: HomePartDefinition): PartSocket[] {
  if (Array.isArray(part.sockets) && part.sockets.length > 0) {
    return part.sockets;
  }
  return defaultSocketsForPart(part);
}

export type SocketSnap = {
  col: number;
  row: number;
  hostPlacementId: string;
  socketId: string;
};

/** Snap prop toward nearest accepting socket within `reach` cells. */
export function findSocketSnap(
  data: HomeRoomData,
  partId: string,
  col: number,
  row: number,
  reach = 1,
): SocketSnap | null {
  const part = partById(data, partId);
  if (!part || normalizePartKind(part.kind) !== "prop") return null;

  let best: SocketSnap | null = null;
  let bestDist = Infinity;

  for (const placement of data.placements) {
    const host = partById(data, placement.partId);
    if (!host || normalizePartKind(host.kind) !== "solid") continue;
    for (const socket of socketsForPart(host)) {
      if (!socket.accept.includes("prop")) continue;
      const sc = placement.gridX + socket.dx;
      const sr = placement.gridY + socket.dy;
      if (sr + 1 < data.gridRows && socket.id === "doorstep") {
        // Prefer cell just in front of solid when possible.
        const frontRow = Math.min(data.gridRows - 1, sr + 1);
        const distFront =
          Math.abs(col - sc) + Math.abs(row - frontRow);
        if (distFront <= reach && distFront < bestDist) {
          bestDist = distFront;
          best = {
            col: sc,
            row: frontRow,
            hostPlacementId: placement.id,
            socketId: `${socket.id}-front`,
          };
        }
      }
      const dist = Math.abs(col - sc) + Math.abs(row - sr);
      if (dist <= reach && dist < bestDist) {
        bestDist = dist;
        best = {
          col: sc,
          row: sr,
          hostPlacementId: placement.id,
          socketId: socket.id,
        };
      }
    }
  }
  return best;
}

export function socketWorldRects(
  data: HomeRoomData,
): Array<GridRect & { placementId: string; socketId: string }> {
  const out: Array<GridRect & { placementId: string; socketId: string }> = [];
  for (const placement of data.placements) {
    const host = partById(data, placement.partId);
    if (!host || normalizePartKind(host.kind) !== "solid") continue;
    for (const socket of socketsForPart(host)) {
      out.push({
        x: placement.gridX + socket.dx,
        y: placement.gridY + socket.dy,
        w: 1,
        h: 1,
        placementId: placement.id,
        socketId: socket.id,
      });
    }
  }
  return out;
}
